<template>
  <div class="content">
    <div v-if="isFormUnavailable" class="unavailable-container">
      <p class="unavailable-message">The form is currently unavailable.</p>
    </div>
    <div v-else class="bg-light p-2 my-2 pb-4 rounded">
      <template v-if="formFetchError">
        <div class="container-fluid">
          <div class="row mt-4 align-items-center">
            <div class="col-12">
              <h1 class="text-center">Invalid Form / Not found</h1>
              <p class="text-center">
                The form you are trying to edit is either not published or
                doesn't exist anymore
              </p>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <!-- <form @submit.prevent="handleSubmit"> -->
        <div class="mx-4">
          <h2 class="mb-2 text-muted text-center">{{ form.title }}</h2>
          <p
            v-if="form.description"
            class="mb-2 text-muted"
            style="text-align: justify"
          >
            {{ form.description }}
          </p>
          <hr />
          <div v-for="(section, idx) in sections" :key="idx">
            <!-- Section Title & Description -->
            <b-card
              v-if="section.title || section.description"
              class="my-3 px-2"
            >
              <h4 v-if="section.title" class="mb-4 text-center">
                {{ section.title }}
              </h4>
              <p
                v-if="section.description"
                class="mb-2"
                style="text-align: justify"
              >
                {{ section.description }}
              </p>
              <div class="mt-3 row">
                <div
                  v-for="question in groupDependentsNextToParents(section.body)"
                  :key="question.key"
                  v-show="shouldRenderQuestion(question)"
                  class="col-12 col-md-6 mb-3"
                >
                  <component
                    :ref="question.key"
                    :is="question.type + '-renderer'"
                    v-bind="question"
                    v-model="testInput[question.key]"
                    @update:modelValue="
                      (val) => (testInput[question.key] = val)
                    "
                  >
                  </component>
                </div>
              </div>
            </b-card>
          </div>
          <hr />
          <div class="d-flex justify-content-end">
            <button
              class="btn btn-success"
              @click="validateForm"
              :disabled="!isFormChanged || !isFormValid"
            >
              Submit
            </button>
          </div>
        </div>
        <!-- </form> -->
      </template>
    </div>
  </div>
</template>

<script>
import LineInputRenderer from "../../FormTemplate/_components/renderers/LineInputRenderer.vue";
import ParagraphInputRenderer from "../../FormTemplate/_components/renderers/ParagraphInputRenderer.vue";
import RatingInputRenderer from "../../FormTemplate/_components/renderers/RatingInputRenderer.vue";
import DropDownInputRenderer from "../../FormTemplate/_components/renderers/DropDownInputRenderer.vue";
import CheckboxInputRenderer from "../../FormTemplate/_components/renderers/CheckboxInputRenderer.vue";
import RadioInputRenderer from "../../FormTemplate/_components/renderers/RadioInputRenderer.vue";
import FileInputRenderer from "../../FormTemplate/_components/renderers/FileInputRenderer.vue";
import DateInputRenderer from "../../FormTemplate/_components/renderers/DateInputRenderer.vue";
import { mapGetters } from "vuex";

export default {
  components: {
    "line-renderer": LineInputRenderer,
    "paragraph-renderer": ParagraphInputRenderer,
    "rating-renderer": RatingInputRenderer,
    "dropdown-renderer": DropDownInputRenderer,
    "checkbox-renderer": CheckboxInputRenderer,
    "radio-renderer": RadioInputRenderer,
    "file-renderer": FileInputRenderer,
    "date-renderer": DateInputRenderer,
  },
  props: {
    formMasterId: {
      type: [String, Number],
      required: false,
    },
    formVersionId: {
      type: [String, Number],
      required: false,
    },
  },
  data() {
    return {
      testInput: {},
      form: {
        sections: [],
      },
      originalInput: {},
      localFormVersionId: this.formVersionId,
      localFormMasterId: this.formMasterId,
    };
  },
  methods: {
    groupDependentsNextToParents(array) {
      const result = [];

      for (const question of array) {
        // take only those question which are not dependent (bcoz dependent question will eventually fall when their parent will come)
        if (!question.showConditionally) {
          // push the non-dependent questions first and then check for its dependent questions
          result.push(question);
          // if the non-dependent question is radio then only check for its group
          if (question.type == "radio")
            for (const q of array) {
              if (
                q.showConditionally &&
                q.conditionalQuestion.key === question.key
              ) {
                result.push(q);
              }
            }
        }
      }

      return result;
    },
    shouldRenderQuestion(question) {
      // render questions which are not conditional
      if (!question.showConditionally) return true;

      // get the conditional question for the current question
      const condition = question.conditionalQuestion;
      if (!condition?.key || condition.value == null) return false;

      // get the value for the conditional question
      const selectedValue = this.testInput[condition.key];

      // check whether the input value for conditional question is same as desired
      return selectedValue === condition.value;
    },
    async validateForm() {
      let isValid = true;
      for (let section of this.sections) {
        const body = section.body;
        for (const question of body) {
          if (
            question.showConditionally &&
            !this.shouldRenderQuestion(question)
          )
            continue;
          if (
            question.type == "file" &&
            typeof this.testInput[question.key] == "string"
          )
            continue;

          const isFieldValid = question.useValidation
            ? this.$refs[question.key][0].checkError(
                this.testInput[question.key]
              )
            : false;

          if (isFieldValid === true) {
            isValid = false;
          }
        }
      }
      if (isValid) {
        try {
          await this.submitFormResponse();
        } catch (err) {
          console.error("Error submitting form:", err);
        }
      } else {
        console.warn("Validation failed");
      }
    },
    initializeTestInput(prefillData = null) {
      const ver = this.version;
      const newTestInput = {};
      const sections = ver.form_json?.sections || [];
      for (let [index, section] of sections.entries()) {
        const sectionKey = section.key || `section_${index + 1}`;
        const sectionResponse = prefillData?.[sectionKey] || {};
        if (Array.isArray(section.body)) {
          for (let question of section.body) {
            const questionKey = question.key;
            let value = sectionResponse[questionKey]?.value;

            if (question.type === "date" && value) {
              const date = new Date(value);
              value = date.toISOString().split("T")[0];
            }

            newTestInput[questionKey] = value !== undefined ? value : "";
          }
        }
      }
      this.testInput = { ...newTestInput };
      this.originalInput = JSON.parse(JSON.stringify(newTestInput)); // Deep copy
    },
    getConfiguredTemplateId() {
      if (!Array.isArray(this.configuredTemplate)) return null;
      const configuredTemplate = this.configuredTemplate.find(
        (template) => template.key === "APPLICANT_JOINING_FORM"
      );
      return configuredTemplate ? configuredTemplate.value[0] : null;
    },
    getSelectedTemplate() {
      const templateId = this.getConfiguredTemplateId();
      if (templateId) {
        const selectedTemplate = this.allForms.find(
          (template) => template.id == templateId
        );
        if (selectedTemplate) {
          this.localFormMasterId = selectedTemplate.id;

          const formMasterLatestVersion = selectedTemplate.formVersion
            .filter((version) => version.status === "P")
            .reduce((latest, current) => {
              return new Date(current.updatedAt) > new Date(latest.updatedAt)
                ? current
                : latest;
            });

          this.localFormVersionId = formMasterLatestVersion.id;

          return selectedTemplate;
        }
      }

      return null;
    },
    trySetFormDetails() {
      if (this.allForms?.length && this.configuredTemplate?.length) {
        this.getSelectedTemplate(); // sets formMasterId and formVersionId
        this.isFormLoading = false;
      }
    },
    async submitFormResponse() {
      let hasFile = false;
      for (const key in this.testInput) {
        const value = this.testInput[key];
        if (value instanceof FileList && value.length > 0) {
          hasFile = true;
          break;
        }
      }

      const responseData = {
        formVersionId: this.joiningDetails?.joiningDetails
          ? this.joiningDetails?.joiningDetails?.formVersionId
          : this.localFormVersionId,
        formMasterId: this.joiningDetails.joiningDetails
          ? this.joiningDetails?.joiningDetails?.formMasterId
          : this.localFormMasterId,
      };

      if (hasFile) {
        const formData = new FormData();
        const joiningFormResponse = {};
        for (let [index, section] of this.sections.entries()) {
          const sectionKey = section.key || `section_${index + 1}`;
          joiningFormResponse[sectionKey] = {};

          for (let question of section.body) {
            const { label, type, key } = question;
            const value = this.testInput[key];
            if (value instanceof FileList && value.length > 0) {
              joiningFormResponse[sectionKey][key] = {
                label,
                type,
                value: value[0],
              };
            } else {
              if (type == "file") {
                const fileValue = value;
                joiningFormResponse[sectionKey][key] = {
                  label,
                  type,
                  value: fileValue,
                  ...(fileValue
                    ? {
                        name: this.joiningDetails?.joiningDetails?.response?.[
                          sectionKey
                        ]?.[key]?.name,
                      }
                    : {}),
                };
              } else
                joiningFormResponse[sectionKey][key] = {
                  label,
                  type,
                  value,
                };
            }
          }
        }

        formData.append("data", JSON.stringify(responseData));
        for (const sectionKey in joiningFormResponse) {
          for (const key in joiningFormResponse[sectionKey]) {
            const { label, type, value } = joiningFormResponse[sectionKey][key];
            if (value instanceof File) {
              formData.append(
                `joining_form_response[${sectionKey}][${key}][file]`,
                value
              );
              formData.append(
                `joining_form_response[${sectionKey}][${key}][label]`,
                label
              );
              formData.append(
                `joining_form_response[${sectionKey}][${key}][type]`,
                type
              );
            } else {
              formData.append(
                `joining_form_response[${sectionKey}][${key}]`,
                type == "file"
                  ? JSON.stringify({
                      label,
                      value,
                      type,
                      ...(value
                        ? {
                            name: this.joiningDetails?.joiningDetails
                              ?.response?.[sectionKey]?.[key]?.name,
                          }
                        : {}),
                    })
                  : JSON.stringify({ label, value, type })
              );
            }
          }
        }

        await this.$store.dispatch("$_applicant_master/submitJoiningResponse", {
          formData,
        });
      } else {
        const structuredResponse = {};

        for (let [index, section] of this.sections.entries()) {
          const sectionKey = section.key || `section_${index + 1}`;
          structuredResponse[sectionKey] = {};

          for (let question of section.body) {
            const { key, label, type } = question;
            structuredResponse[sectionKey][key] = {
              label,
              type,
              value: this.testInput[key],
            };
            if (type == "file") {
              const fileValue = this.testInput[key];
              const name =
                fileValue &&
                this.joiningDetails?.joiningDetails?.response?.[sectionKey]?.[
                  key
                ]?.name;
              if (fileValue && name) {
                structuredResponse[sectionKey][key].name = name;
              }
            }
          }
        }
        responseData.joining_form_response = structuredResponse;
        await this.$store.dispatch("$_applicant_master/submitJoiningResponse", {
          data: responseData,
        });
      }
    },
  },
  computed: {
    ...mapGetters({
      joiningDetails: "$_applicant_master/getJoiningDetails",
      configuredTemplate: "system_config/systemConfigs",
      allForms: "$_form_template/getForms",
      formFetchError: "$_form_template/getFormFetchError",
      version: "$_form_template/getEditorActiveFormVersion",
    }),
    sections() {
      if (!this.form) {
        return [];
      }
      return this.form.sections;
    },
    isFormChanged() {
      return (
        JSON.stringify(this.testInput) !== JSON.stringify(this.originalInput)
      );
    },
    // Computed property to determine if the form is valid
    isFormValid() {
      for (let section of this.sections) {
        const body = section.body;
        for (const question of body) {
          if (
            question.showConditionally &&
            !this.shouldRenderQuestion(question)
          )
            continue;

          if (
            question.type === "file" &&
            (this.testInput[question.key] === "" ||
              this.testInput[question.key] === null ||
              typeof this.testInput[question.key] === "string")
          )
            continue;

          const hasError = question.useValidation
            ? this.$refs[question.key][0].checkError(
                this.testInput[question.key],
                false
              )
            : false;

          if (hasError === true) {
            return false;
          }
        }
      }
      return true;
    },
    isFormUnavailable() {
      const templateId = this.getConfiguredTemplateId();
      if (!templateId || !this.allForms || !Array.isArray(this.allForms)) {
        return true;
      }

      const selectedTemplate = this.allForms.find(
        (template) => template.id == templateId
      );
      if (!selectedTemplate) return true;
      if (selectedTemplate.visibility === "private") return true;

      return false;
    },
  },
  watch: {
    joiningDetails: {
      handler(newVal) {
        const prefillData = newVal?.joiningDetails?.response || null;
        this.initializeTestInput(prefillData);
        if (newVal?.joiningDetails) {
          this.localFormVersionId = newVal?.joiningDetails?.formVersionId;
          this.localFormMasterId = newVal?.joiningDetails?.formMasterId;
        } else {
          this.trySetFormDetails();
        }
      },
      immediate: true,
    },
    // formVersionId: {
    //   immediate: true,
    //   handler(newVal) {
    //     console.log("id", newVal);
    //     this.localFormVersionId = newVal;
    //   },
    // },
    localFormVersionId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.$store.dispatch(
            "$_form_template/fetchEditorFormVersion",
            newVal
          );
        } else this.trySetFormDetails();
      },
    },
    allForms: {
      immediate: true,
      handler() {
        if (!this.joiningDetails?.joiningDetails) this.trySetFormDetails();
      },
    },
    version: {
      immediate: true,
      handler(version) {
        if (!version) return;

        if (typeof version.form_json === "string") {
          this.form = JSON.parse(version.form_json);
        } else {
          this.form = version.form_json;
        }

        // Initialize test input after form is updated
        const prefillData =
          this.joiningDetails?.joiningDetails?.response || null;
        this.initializeTestInput(prefillData);
      },
    },
    // configuredTemplate: {
    //   immediate: true,
    //   handler() {
    //     if (!this.joiningDetails?.joiningDetails) this.trySetFormDetails();
    //   },
    // },
  },
  async mounted() {
    await this.$store.dispatch("$_applicant_master/getJoiningDetails");
    await this.$store.dispatch("system_config/fetchSystemConfigs");
    await this.$store.dispatch("$_form_template/getAllFormMasters");
    if (this.localFormVersionId) {
      await this.$store.dispatch(
        "$_form_template/fetchEditorFormVersion",
        this.localFormVersionId
      );
    }
    const prefillData = this.joiningDetails?.joiningDetails?.response || null;
    this.initializeTestInput(prefillData);
  },
};
</script>

<style scoped>
/* .form-container { */
/* max-width: 720px; */
/* margin: auto;
} */

.card-body {
  padding: 1rem !important;
}

.section-title {
  font-size: 1rem;
  cursor: pointer;
}

.col-12,
.col-md-6 {
  padding-left: 2rem;
  padding-right: 2rem;
}
.unavailable-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh; /* full screen height */
  text-align: center;
}
.unavailable-message {
  color: #666;
  font-weight: bold;
  font-size: 1.5rem;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
}
</style>
